// 홈 화면 모달·뷰어 — 실행 모달(회신 초안), 메일 원문 뷰어, 리스 상환 스케줄 팝업
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AXButton,
  AXKeyValueGrid,
  AXKeyValueRow,
  AXPanelHeader,
  AXProgressBar,
  AXSegmentedControl,
} from 'yukimushi-component-fe';
import { ModalIcon } from './Home.icons';
import { fmtMoney, fmtMoneyText } from 'yukimushi-component-fe';
import { REPAYMENT_SCHEDULE_DATA } from './Home.data';
import type { ActionModalConfig } from './Home.types';
import styles from './Home.startview.module.css';

/* ── 실행 모달 (재사용 가능) ── */
export function ActionModal({
  config,
  onClose,
}: {
  config: ActionModalConfig;
  onClose: () => void;
}) {
  const [lang, setLang] = useState<'ko' | 'ja'>('ko');
  const [done, setDone] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  /* 언어별 독립 편집 상태 */
  const [draftKo, setDraftKo] = useState(config.mailReply?.ko ?? '');
  const [draftJa, setDraftJa] = useState(config.mailReply?.ja ?? '');
  /* 회신 초안 로딩 상태 */
  const [draftReady, setDraftReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDraftReady(true), 2000);
    return () => clearTimeout(t);
  }, []);

  /* Escape 키 닫기 */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  /* 모달 내부 wheel 이벤트가 페이지 섹션 스크롤로 버블링되는 것을 차단 */
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener('wheel', stop, { passive: false });
    return () => el.removeEventListener('wheel', stop);
  }, []);

  const handlePrimary = () => {
    setDone(true);
    setTimeout(onClose, 1200);
  };

  return (
    <div ref={overlayRef} className={styles.actionModalOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.actionModal} role="dialog" aria-modal="true">

        {/* 헤더 */}
        <div className={styles.actionModalHdr}>
          <div className={styles.actionModalIcon}><ModalIcon type={config.iconType} /></div>
          <div className={styles.actionModalHdrTxt}>
            <div className={styles.actionModalLabel}>{config.label}</div>
            <div className={styles.actionModalTitle}>{config.title}</div>
          </div>
          <button className={styles.actionModalClose} onClick={onClose} aria-label="닫기">×</button>
        </div>

        {/* 본문 */}
        <div className={styles.actionModalBody}>
          {/* 요약 */}
          <div className={styles.actionModalSummary}>{config.summary}</div>

          {/* 통계 칩 */}
          {config.stats.length > 0 && (
            <div className={styles.actionModalGrid}>
              {config.stats.map((s, i) => (
                <div key={i} className={styles.actionModalStat}>
                  <b>{s.value}</b><span>{s.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* mail-reply 전용: 언어 토글 + 회신 초안 */}
          {config.mailReply && (
            <div className={styles.actionModalDoc}>
              <div className={styles.actionModalDocTitle}>
                <AXPanelHeader
                  icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0F172A" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
                  title="회신 초안"
                  titleSize={13}
                  titleWeight={800}
                  trailing={
                    <AXSegmentedControl
                      size="sm"
                      variant="outline"
                      value={lang}
                      onChange={v => setLang(v as 'ko' | 'ja')}
                      options={[
                        { value: 'ko', label: '한국어' },
                        { value: 'ja', label: '日本語' },
                      ]}
                    />
                  }
                />
              </div>
              {draftReady ? (
                <textarea
                  className={styles.actionModalDocBody}
                  value={lang === 'ko' ? draftKo : draftJa}
                  onChange={e => lang === 'ko' ? setDraftKo(e.target.value) : setDraftJa(e.target.value)}
                  onWheel={e => e.stopPropagation()}
                  spellCheck={false}
                />
              ) : (
                <div className={styles.actionModalDocLoading}>
                  <div className={styles.actionModalSkelLine} style={{ width: '88%' }} />
                  <div className={styles.actionModalSkelLine} style={{ width: '72%' }} />
                  <div className={styles.actionModalSkelLine} style={{ width: '95%' }} />
                  <div className={styles.actionModalSkelLine} style={{ width: '60%' }} />
                  <div className={styles.actionModalSkelLine} style={{ width: '80%' }} />
                  <div className={styles.actionModalSkelLine} style={{ width: '50%' }} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className={styles.actionModalFooter}>
          <AXButton variant="outline" onClick={onClose}>취소</AXButton>
          <AXButton
            gradient={done ? undefined : ['#7C3AED', '#3B82F6']}
            background={done ? '#ECFDF5' : undefined}
            color={done ? '#047857' : '#fff'}
            borderColor={done ? '#A7F3D0' : undefined}
            onClick={handlePrimary}
            disabled={done}
          >
            {done
              ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>{config.doneLabel}</>
              : config.primaryLabel
            }
          </AXButton>
        </div>
      </div>
    </div>
  );
}

/* ── 메일 원문 뷰어 (요약 + 한글/일어 토글) ── */
export function MailBodyViewer({
  summary,
  body,
}: {
  summary: { label: string; value: string }[];
  body: { ko: string; ja: string };
}) {
  const [lang, setLang] = useState<'ko' | 'ja'>('ko');
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={styles.mailViewer}>
      {/* 요약 섹션 */}
      <div className={styles.mailSummaryBlock}>
        <div className={styles.mailSummaryHeader}>
          <AXPanelHeader
            icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2.2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 8l10 7 10-7"/></svg>}
            title="메일 요약"
            titleSize={12}
            titleWeight={600}
          />
        </div>
        <div className={styles.mailSummaryRows}>
          <AXKeyValueRow rows={summary.map(item => ({ label: item.label, value: item.value }))} />
        </div>
      </div>

      {/* 본문 섹션 */}
      <div className={styles.mailBodyBlock}>
        <div className={styles.mailBodyHeader}>
          <AXPanelHeader
            icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
            title="메일 본문"
            titleSize={12}
            titleWeight={600}
            trailing={
              <AXSegmentedControl
                size="sm"
                variant="outline"
                value={lang}
                onChange={v => setLang(v as 'ko' | 'ja')}
                options={[
                  { value: 'ko', label: '한국어' },
                  { value: 'ja', label: '日本語' },
                ]}
              />
            }
          />
        </div>
        <div className={`${styles.mailBodyText} ${expanded ? styles.mailBodyExpanded : ''}`}>
          {body[lang].split('\n').map((line, i) => (
            <span key={i}>{line}<br /></span>
          ))}
        </div>
        <button className={styles.mailExpandBtn} onClick={() => setExpanded(v => !v)}>
          {expanded ? '접기 ▲' : '전체 보기 ▼'}
        </button>
      </div>
    </div>
  );
}

/* ── 상환 스케줄 확인 팝업 (MOU 회신 메일 팝업 디자인 참고) ── */
export function RepaymentScheduleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const rows = useMemo(() => {
    let lastYear = '';
    const built: ReactNode[] = [];
    REPAYMENT_SCHEDULE_DATA.forEach(r => {
      const [no, date, beg, intr, prin, lease, tax, cash, end] = r;
      const year = date.slice(0, 4);
      if (year !== lastYear) {
        lastYear = year;
        built.push(
          <tr key={`y${year}`} className={styles.repayYearRow}><td colSpan={9}>{year}년</td></tr>
        );
      }
      built.push(
        <tr key={no} className={no === 1 ? styles.repayRowCurrent : ''}>
          <td className={`${styles.repayTdC} ${styles.repayTdNo}`}>{no}</td>
          <td className={styles.repayTdL}>{date}{no === 1 && <span className={styles.repayChip}>✓ 분개 완료</span>}</td>
          <td className={styles.repayTdMuted}>{fmtMoney(beg)}</td>
          <td>{fmtMoney(intr)}</td>
          <td>{fmtMoney(prin)}</td>
          <td>{fmtMoney(lease)}</td>
          <td>{fmtMoney(tax)}</td>
          <td className={styles.repayTdStrong}>{fmtMoney(cash)}</td>
          <td className={styles.repayTdMuted}>{fmtMoney(end)}</td>
        </tr>
      );
    });
    return built;
  }, []);

  const first = REPAYMENT_SCHEDULE_DATA[0];

  return (
    <div
      className={`${styles.repayOverlay} ${open ? styles.repayOverlayOpen : ''}`}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.repayModal} role="dialog" aria-modal="true" aria-label="상환 스케줄 확인">
        {/* 헤더 */}
        <div className={styles.repayHdr}>
          <div className={styles.repayHdrIc}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" fill="#fff"/><path d="M18.5 15l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6z" fill="#bfd7ff"/></svg>
          </div>
          <div className={styles.repayHdrTx}>
            <div className={styles.repayTitle}>상환 스케줄</div>
            <div className={styles.repaySubtitle}>복합기 리스 · <b>株式会社 大塚商会</b> · 금융리스(원칙법) · 계약기간 2026-06-01 ~ 2031-05-31</div>
          </div>
          <span className={styles.repayLiveBadge}><span className={styles.repayLiveDot} />1회차 전표 생성됨</span>
          <button className={styles.repayClose} onClick={onClose} aria-label="닫기">×</button>
        </div>

        {/* 요약 카드 */}
        <div className={styles.repaySummary}>
          <AXKeyValueGrid
            columns={5}
            primaryFirst
            primaryBackground="linear-gradient(135deg, #2F6BF0, #2554D8)"
            primaryLabelColor="rgba(255,255,255,.75)"
            primaryValueColor="#fff"
            primaryNoteColor="rgba(255,255,255,.65)"
            items={[
              { label: '리스 대상 금액', value: '907,700 JPY', note: '세전 · 실효 연이자율 4.54%' },
              { label: '월 현금지급', value: '18,590 JPY', note: '리스료 16,900 JPY + 소비세 1,690 JPY' },
              { label: '총 현금지급', value: '1,115,404 JPY', note: '리스료 1,014,000 JPY + 소비세 101,400 JPY' },
              { label: '총 이자비용', value: '106,304 JPY', note: '총 리스료 − 리스 대상 금액' },
              { label: '상환 기간', value: '60회 / 5년', note: '매월 3일 · 자동 이체' },
            ]}
          />
        </div>

        {/* 진행률 */}
        <div className={styles.repayProgressWrap}>
          <span className={styles.repayProgressLabel}>납입 진행 <b>1</b> / 60회</span>
          <AXProgressBar value={1} max={60} size="sm" gradient={['#3B82F6', '#2563EB']} />
          <span className={styles.repayProgressLabel}>잔여 리스채무 <b>894,163 JPY</b></span>
        </div>

        {/* 툴바 */}
        <div className={styles.repayToolbar}>
          <span className={styles.repayBasisPill}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M3 3v18h18"/><path d="M7 14l4-5 4 3 5-7"/></svg>
            실효이자율(EIR) 기준
          </span>
          <span className={styles.repayToolbarNote}>매월 이자비용·원금상환이 변동됩니다 · 소비세 10% 별도(가수 소비세)</span>
          <div style={{ flex: 1 }} />
          <AXButton variant="outline" size="sm" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}>
            Excel 다운로드
          </AXButton>
          <AXButton variant="outline" size="sm" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>}>
            인쇄
          </AXButton>
        </div>

        {/* 테이블 */}
        <div className={styles.repayTableScroll}>
          <table className={styles.repayTable}>
            <thead>
              <tr>
                <th className={styles.repayThC} style={{ width: 56 }}>회차</th>
                <th className={styles.repayThL} style={{ width: 100 }}>지급일</th>
                <th>기초 리스채무(JPY)</th>
                <th>이자비용(JPY)</th>
                <th>리스채무 상환(JPY)</th>
                <th>월 리스료(세전)(JPY)</th>
                <th>소비세(JPY)</th>
                <th>현금지급(JPY)</th>
                <th>기말 리스채무(JPY)</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className={styles.repayThL}>합계</td>
                <td>106,304</td>
                <td>907,700</td>
                <td>1,014,000</td>
                <td>101,400</td>
                <td>1,115,404</td>
                <td>—</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* 푸터 */}
        <div className={styles.repayFooter}>
          <span className={styles.repayFootNote}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <span>
              1회차 분개: 차변 리스채무 {fmtMoneyText(first[4], 'JPY')} · 이자비용 {fmtMoneyText(first[3], 'JPY')} · 가수 소비세 {fmtMoneyText(first[6], 'JPY')} / 대변 보통예금 {fmtMoneyText(first[7], 'JPY')}<br />
              본 스케줄은 금융리스 가정(실효 연이자율 4.54%)으로 작성되었으며, 확정 시 리스사 공식 원리금 내역으로 보정이 필요합니다.
            </span>
          </span>
          <div style={{ flex: 1 }} />
          <button className={styles.repayBtn} onClick={onClose}>닫기</button>
          <button className={`${styles.repayBtn} ${styles.repayBtnPrimary}`} onClick={onClose}>전표 화면으로 돌아가기</button>
        </div>
      </div>
    </div>
  );
}
